import React, { useState, useCallback, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  scratchpadClient,
  type SessionScratchpad,
} from '@/lib/api/scratchpad.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import type { EventLogEntry, SessionHistoryEntry } from '@super/shared-types'

// Same-timestamp tie-break: lifecycle order, CALL_END always last.
const LIFECYCLE_ORDER = [
  'DISPATCH',
  'CALL_START',
  'RING_START',
  'RING_END',
  'AGENT_START',
  'AGENT_END',
]
function lifecycleRank(eventType: string): number {
  if (eventType === 'CALL_END') return Number.MAX_SAFE_INTEGER
  const i = LIFECYCLE_ORDER.indexOf(eventType)
  return i === -1 ? LIFECYCLE_ORDER.length : i
}

interface ScratchpadCardProps {
  sessionId: string
}

function ConsumerLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-500 shrink-0" />
      <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
        {children}
      </span>
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5">
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-xs leading-relaxed">
      <span className="text-gray-500 dark:text-muted-foreground shrink-0 w-28">
        {label}
      </span>
      <span className="text-gray-900 dark:text-foreground">{value}</span>
    </div>
  )
}

function formatDate(iso: string, timeZone?: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone && { timeZone }),
  })
}

function PriorCallRow({
  entry,
  timeZone,
}: {
  entry: SessionHistoryEntry
  timeZone?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const title = entry.title || entry.summary
  const hasSummary = !!(entry.title && entry.summary)
  const toggle = useCallback(() => setExpanded(v => !v), [])

  return (
    <div className="py-0.5">
      <button
        className="w-full flex gap-2 text-xs items-baseline text-left"
        onClick={hasSummary ? toggle : undefined}
      >
        <span className="text-gray-500 dark:text-muted-foreground shrink-0 w-28">
          {formatDate(entry.date, timeZone)}
        </span>
        <span className="text-gray-900 dark:text-foreground flex-1">
          {title}
        </span>
        {entry.isResolved && (
          <span className="text-[10px] text-gray-400 dark:text-muted-foreground shrink-0">
            resolved
          </span>
        )}
        {hasSummary && (
          <ChevronDown
            className={cn(
              'h-3 w-3 text-gray-400 shrink-0 transition-transform',
              expanded && 'rotate-180',
            )}
          />
        )}
      </button>
      {expanded && entry.summary && (
        <p className="mt-1 ml-28 text-xs text-gray-500 dark:text-muted-foreground leading-relaxed">
          {entry.summary}
        </p>
      )}
    </div>
  )
}

function EventRow({
  entry,
  timeZone,
}: {
  entry: EventLogEntry
  timeZone?: string
}) {
  const params = entry.parameters ?? {}
  const detail =
    (params.url as string) ?? (params.linkName as string) ?? entry.eventType
  const time = new Date(entry.timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    ...(timeZone && { timeZone }),
  })
  return (
    <div className="flex gap-2 text-xs leading-relaxed">
      <span className="text-gray-500 dark:text-muted-foreground shrink-0 w-28">
        {entry.eventType}
      </span>
      <span className="text-gray-900 dark:text-foreground truncate flex-1">
        {detail}
      </span>
      <span className="text-gray-400 dark:text-muted-foreground shrink-0">
        {time}
      </span>
      <span className={entry.successful ? 'text-green-500' : 'text-red-500'}>
        {entry.successful ? '✓' : '✗'}
      </span>
    </div>
  )
}

type EventBlock =
  | { kind: 'single'; entry: EventLogEntry }
  | {
      kind: 'staffLeg'
      attemptNumber: number
      phoneNumber?: string
      entries: EventLogEntry[]
    }

/** Groups consecutive SEGMENT (staff-leg) events sharing a connectionId into one
 * block, so a phone tree with multiple attempts renders each as its own labeled
 * group instead of an unlabeled, indistinguishable run of repeated event types. */
function groupEventLog(entries: EventLogEntry[]): EventBlock[] {
  const blocks: EventBlock[] = []
  let staffLegCount = 0
  for (const entry of entries) {
    const params = entry.parameters ?? {}
    const connectionId = params.connectionId as string | undefined
    if (params.connectionType === 'SEGMENT' && connectionId) {
      const last = blocks[blocks.length - 1]
      const phoneNumber = params.phoneNumber as string | undefined
      if (
        last?.kind === 'staffLeg' &&
        last.entries[0].parameters?.connectionId === connectionId
      ) {
        last.entries.push(entry)
        if (!last.phoneNumber && phoneNumber) {
          last.phoneNumber = phoneNumber
        }
        continue
      }
      staffLegCount += 1
      blocks.push({
        kind: 'staffLeg',
        attemptNumber: staffLegCount,
        phoneNumber,
        entries: [entry],
      })
      continue
    }
    blocks.push({ kind: 'single', entry })
  }
  return blocks
}

function StaffLegGroup({
  attemptNumber,
  showAttemptNumber,
  phoneNumber,
  entries,
  timeZone,
}: {
  attemptNumber: number
  showAttemptNumber: boolean
  phoneNumber?: string
  entries: EventLogEntry[]
  timeZone?: string
}) {
  return (
    <div className="border-l-2 border-blue-300 dark:border-blue-700 pl-2 flex flex-col gap-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-500 dark:text-blue-400">
        Staff leg{showAttemptNumber && ` · attempt ${attemptNumber}`}
        {phoneNumber && ` — ${phoneNumber}`}
      </div>
      {entries.map((entry, i) => (
        <EventRow key={i} entry={entry} timeZone={timeZone} />
      ))}
    </div>
  )
}

export function ScratchpadCard({ sessionId }: ScratchpadCardProps) {
  const { currentCompany } = useAppAuth()
  const timeZone = currentCompany?.timezoneName
  const [data, setData] = useState<SessionScratchpad | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const toggle = useCallback(() => setOpen(v => !v), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    scratchpadClient
      .getBySession(sessionId)
      .then((res: SessionScratchpad) => {
        if (!cancelled) setData(res)
      })
      .catch((err: unknown) => {
        console.error('[ScratchpadCard] fetch failed:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sessionId])

  if (loading) return null
  if (!data) return null

  const {
    initial = null,
    mentioned = null,
    eventLog = [],
    contextPrompt = null,
    intakeLog = null,
  } = data

  // ── Inbound agent · session start ──────────────────────────────────────
  const crmName =
    initial?.context.contact.name.crm &&
    initial.context.contact.name.crm !== 'Unknown Contact'
      ? initial.context.contact.name.crm
      : null
  const crmEmail = initial?.context.contact.email?.crm ?? null
  const phone = initial?.context.contact.phone ?? null
  const hasCallerContext = !!(crmName || crmEmail || phone)

  const crmProperties = initial?.context.properties.crm ?? []

  const priorSessions = (initial?.sessionHistory?.entries ?? []).filter(
    e => e.title || e.summary,
  )

  const hasAgentContext =
    hasCallerContext || crmProperties.length > 0 || priorSessions.length > 0

  // ── Extracted this session ──────────────────────────────────────────────
  const mentionedName = mentioned?.contact?.name ?? null
  const mentionedEmail = mentioned?.contact?.email ?? null
  const mp = mentioned?.properties?.mentioned
  const hasMentionedProperty = !!(
    mp?.name ||
    mp?.address ||
    mp?.unit ||
    mp?.type
  )
  const hasMentioned = !!(
    mentionedName ||
    mentionedEmail ||
    hasMentionedProperty
  )

  // ── Events ──────────────────────────────────────────────────────────────
  const hasEvents = eventLog.length > 0
  const sortedEventLog = [...eventLog].sort(
    (a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime() ||
      lifecycleRank(a.eventType) - lifecycleRank(b.eventType),
  )

  const hasIntakeLog = !!(intakeLog && intakeLog.length > 0)

  if (!hasAgentContext && !hasMentioned && !hasEvents && !hasIntakeLog)
    return null

  const chips: string[] = []
  if (priorSessions.length > 0)
    chips.push(
      `${priorSessions.length} prior session${priorSessions.length !== 1 ? 's' : ''}`,
    )
  if (contextPrompt) chips.push('fill policy')
  if (hasIntakeLog)
    chips.push(
      `${intakeLog!.length} intake turn${intakeLog!.length !== 1 ? 's' : ''}`,
    )
  if (hasMentioned) {
    const n = [
      mentionedName,
      mentionedEmail,
      mp?.name,
      mp?.address,
      mp?.type,
    ].filter(Boolean).length
    chips.push(`${n} mention${n !== 1 ? 's' : ''}`)
  }
  if (hasEvents)
    chips.push(`${eventLog.length} event${eventLog.length !== 1 ? 's' : ''}`)

  return (
    <div className="bg-gray-50 dark:bg-muted border border-gray-200 dark:border-border rounded-lg shadow-sm overflow-hidden mt-2">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
          <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
            Scratchpad
          </span>
          {chips.map(chip => (
            <span
              key={chip}
              className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400"
            >
              {chip}
            </span>
          ))}
        </div>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-border pt-2.5 flex flex-col gap-4">
          {/* ── Inbound agent · session start ─────────────────────────── */}
          {hasAgentContext && (
            <div>
              <ConsumerLabel>Session start</ConsumerLabel>
              <div className="flex flex-col gap-2">
                {hasCallerContext && (
                  <div>
                    <SectionLabel>Caller context</SectionLabel>
                    <div className="flex flex-col gap-0.5">
                      {crmName && <Row label="Name" value={crmName} />}
                      {phone && <Row label="Phone" value={phone} />}
                      {crmEmail && <Row label="Email" value={crmEmail} />}
                    </div>
                  </div>
                )}

                {crmProperties.length > 0 && (
                  <div>
                    <SectionLabel>Properties</SectionLabel>
                    <div className="flex flex-col gap-0.5">
                      {crmProperties.map((p, i) => (
                        <Row
                          key={i}
                          label={p.type ?? 'Property'}
                          value={[p.name, p.address, p.unit]
                            .filter(Boolean)
                            .join(', ')}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {priorSessions.length > 0 && (
                  <div>
                    <SectionLabel>Prior sessions</SectionLabel>
                    <div className="flex flex-col divide-y divide-gray-100 dark:divide-border/50">
                      {priorSessions.map(entry => (
                        <PriorCallRow
                          key={entry.sessionId}
                          entry={entry}
                          timeZone={timeZone}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Fill policy ──────────────────────────────────────────── */}
          {contextPrompt && (
            <div>
              <ConsumerLabel>Resolved instructions sent to agent</ConsumerLabel>
              <pre className="text-[10px] leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono bg-gray-100 dark:bg-gray-900 rounded p-2">
                {contextPrompt}
              </pre>
            </div>
          )}

          {/* ── Intake whispers (per turn) ────────────────────────── */}
          {hasIntakeLog && (
            <div>
              <ConsumerLabel>Intake instruction per LLM turn</ConsumerLabel>
              <ol className="flex flex-col gap-0.5 list-none">
                {intakeLog!.map((step, i) => {
                  const changed = i === 0 || step !== intakeLog![i - 1]
                  return (
                    <li key={i} className="flex gap-2 text-xs">
                      <span className="text-gray-400 dark:text-muted-foreground shrink-0 w-10 text-right tabular-nums">
                        T{i + 1}
                      </span>
                      <span
                        className={
                          changed
                            ? 'text-gray-900 dark:text-foreground'
                            : 'text-gray-400 dark:text-muted-foreground'
                        }
                      >
                        {step}
                      </span>
                    </li>
                  )
                })}
              </ol>
            </div>
          )}

          {/* ── Extracted this session ────────────────────────────────── */}
          {hasMentioned && (
            <div>
              <ConsumerLabel>Extracted this session</ConsumerLabel>
              <div className="flex flex-col gap-0.5">
                {mentionedName && <Row label="Name" value={mentionedName} />}
                {mentionedEmail && <Row label="Email" value={mentionedEmail} />}
                {mp?.name && <Row label="Property" value={mp.name} />}
                {mp?.address && <Row label="Address" value={mp.address} />}
                {mp?.unit && <Row label="Unit" value={mp.unit} />}
                {mp?.type && <Row label="Role" value={mp.type} />}
              </div>
            </div>
          )}

          {/* ── Events ────────────────────────────────────────────────── */}
          {hasEvents && (
            <div>
              <ConsumerLabel>Events</ConsumerLabel>
              <div className="flex flex-col gap-1.5">
                {(() => {
                  const blocks = groupEventLog(sortedEventLog)
                  const totalStaffLegs = blocks.filter(
                    b => b.kind === 'staffLeg',
                  ).length
                  return blocks.map((block, i) =>
                    block.kind === 'single' ? (
                      <EventRow
                        key={i}
                        entry={block.entry}
                        timeZone={timeZone}
                      />
                    ) : (
                      <StaffLegGroup
                        key={i}
                        attemptNumber={block.attemptNumber}
                        showAttemptNumber={totalStaffLegs > 1}
                        phoneNumber={block.phoneNumber}
                        entries={block.entries}
                        timeZone={timeZone}
                      />
                    ),
                  )
                })()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
